import * as t from 'io-ts';
import { NonEmptyString } from 'io-ts-types';
import { fromEnum } from '../../responseUtil';
import { OptionalAddressCodec } from '../address.types';
import { ApiAccessoryType, CollectedAccessoriesCodec } from './accessoriesApi.types';
import { ApiServiceType } from './serviceTypesApi.types';
import { ServiceVehicleCodec } from './serviceVehicleApi.types';

export enum ApiServiceOrderStatus {
    OPEN = 'open',
    COMPLETED = 'completed',
    CANCELLED = 'cancelled',
}

export enum ServiceOrderApiSort {
    STATUS_ASC = '+status',
    STATUS_DES = '-status',
    STARTED_AT_DESC = '-started_at',
    STARTED_AT_ASC = '+started_at',
    COMPLETED_AT_DESC = '-completed_at',
    COMPLETED_AT_ASC = '+completed_at',
    TYPE_ASC = '+type',
    TYPE_DESC = '-type',
    ORDER_ISSUED_AT_ASC = '+order_issued_at',
    ORDER_ISSUED_AT_DESC = '-order_issued_at',
    LOCATION_IDENTIFIER_ASC = '+location_identifier',
    LOCATION_IDENTIFIER_DESC = '-location_identifier',
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ServiceOrderQueryParameterCodec = t.partial({
    service_location: t.string,
    statuses: t.array(fromEnum<ApiServiceOrderStatus>('ApiServiceOrderStatus', ApiServiceOrderStatus)),
    service_types: t.array(fromEnum<ApiServiceType>('ApiServiceType', ApiServiceType)),
    order_issued_at_after: t.string,
    order_issued_at_before: t.string,
    archived: t.boolean,
    q: t.string,
    sort: fromEnum<ServiceOrderApiSort>('ServiceOrderApiSort', ServiceOrderApiSort),
    limit: t.number,
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ServiceOrderExcelQueryParameterCodec = t.partial({
    service_location: t.string,
    statuses: t.array(fromEnum<ApiServiceOrderStatus>('ApiServiceOrderStatus', ApiServiceOrderStatus)),
    service_types: t.array(fromEnum<ApiServiceType>('ApiServiceType', ApiServiceType)),
    order_issued_at_after: t.string,
    order_issued_at_before: t.string,
});

const ServiceOrderVehicleCodec = t.intersection([
    ServiceVehicleCodec,
    t.type({
        requested_accessories: t.array(fromEnum<ApiAccessoryType>('ApiServiceAccessories', ApiAccessoryType)),
    }),
    t.partial({
        collected_accessories: CollectedAccessoriesCodec,
    }),
]);

export type ApiServiceOrderVehicle = t.TypeOf<typeof ServiceOrderVehicleCodec>;

const LocationCodec = t.intersection([
    t.type({
        time_zone: t.string,
    }),
    t.partial({
        identifier: t.string,
        address: OptionalAddressCodec,
    }),
]);

export type ApiLocation = t.TypeOf<typeof LocationCodec>;

const ServiceOrderCodec = t.intersection([
    t.type({
        id: NonEmptyString,
        external_order_id: NonEmptyString,
        vehicle: ServiceOrderVehicleCodec,
        status: fromEnum<ApiServiceOrderStatus>('ApiServiceOrderStatus', ApiServiceOrderStatus),
        type: fromEnum<ApiServiceType>('ApiServiceType', ApiServiceType),
        service_location: LocationCodec,
        has_unacknowledged_external_changes: t.boolean,
        is_finalized: t.boolean,
        is_archived: t.boolean,
        order_issued_at: t.string,
    }),
    t.partial({
        started_at: t.string,
        completed_at: t.string,
        general_information: t.string,
        description: t.string,
        external_option_id: NonEmptyString,
    }),
]);

export type ApiServiceOrder = t.TypeOf<typeof ServiceOrderCodec>;

export const ServiceOrderPageCodec = t.type({
    items: t.array(ServiceOrderCodec),
    has_more: t.boolean,
});

export type ApiServiceOrderPage = t.TypeOf<typeof ServiceOrderPageCodec>;
export type ApiServiceOrderQueryParameter = t.TypeOf<typeof ServiceOrderQueryParameterCodec>;
export type ApiServiceOrderExcelQueryParameter = t.TypeOf<typeof ServiceOrderExcelQueryParameterCodec>;
